import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  brokers,
  quoteFinancial,
  type OperationDetail,
  type QuoteCoinsuranceType,
  type QuoteFinancial,
  type QuoteFinancialInput,
  type QuoteIssuanceKind,
  type QuoteValidationKind,
} from '@/shared/api/resources';
import {
  formatCivilDate,
  formatDateTime,
  formatRate,
  parseRate,
  rateToInput,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import {
  chaveDoFinanceiro,
  regrasPendentesDoErro,
  usePermissoesDoFinanceiro,
} from './financeiroDaCotacao';

/**
 * Cocorretagem, resseguradores, cosseguro, marcas de emissao e as validacoes
 * automaticas da cotacao (P-COT-4). Ver docs/ADR/0051-financeiro-da-cotacao.md.
 *
 * A participacao e um percentual em pontos-base inteiros: mais de duas casas nao cabem e
 * nao sao arredondadas (TEC-N02). A soma nao e conferida e nao muda premio nem comissao.
 * As validacoes de PEP, resseguro e subscricao respondem com as regras pendentes; as de
 * retroatividade, cosseguro e prazo mostram o que existe, sem decidir.
 */
export function FinancialPanels({
  operacao,
  financeiro,
}: {
  operacao: OperationDetail;
  financeiro: QuoteFinancial;
}) {
  return (
    <>
      <FinancialSharesCard operacao={operacao} financeiro={financeiro} />
      <FinancialIssuanceCard operacao={operacao} financeiro={financeiro} />
      <FinancialValidationsCard operacao={operacao} />
    </>
  );
}

const mensagemDaParticipacao =
  'A participacao vai de 0,01% a 100%, com ate duas casas: mais casas nao cabem em ponto-base e nao sao arredondadas (TEC-N02).';

/** Le o percentual digitado em pontos-base, ou null quando nao cabe. */
function participacaoEmPontos(texto: string): number | null {
  const pontos = parseRate(texto);
  if (pontos === null || pontos < 1 || pontos > 10_000) return null;
  return pontos;
}

interface LinhaDeParte {
  chave: string;
  id: string;
  participacao: string;
}

function FinancialSharesCard({
  operacao,
  financeiro,
}: {
  operacao: OperationDetail;
  financeiro: QuoteFinancial;
}) {
  const permissoes = usePermissoesDoFinanceiro(operacao);
  const [editando, setEditando] = useState<'cocorretagem' | 'resseguradores' | null>(
    null,
  );
  const podeEditar = permissoes.resseguro && financeiro.plan_editable;

  return (
    <Card>
      <CardHeader
        title="Cocorretagem e resseguradores"
        description="Corretoras e resseguradores da cotacao, com a participacao em percentual. A soma nao e conferida e nao muda premio nem comissao: a divisao ainda nao tem regra."
      />
      <CardBody className="grid gap-6 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-ink-900">Cocorretagem</p>
          {financeiro.co_brokers.length === 0 ? (
            <p className="text-sm text-ink-500">Sem cocorretagem.</p>
          ) : (
            <ul className="flex flex-col gap-1 text-sm text-ink-700">
              {financeiro.co_brokers.map((item) => (
                <li key={item.id}>
                  {item.broker.label} · {formatRate(item.share_bps)}
                </li>
              ))}
            </ul>
          )}
          {podeEditar ? (
            <div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEditando('cocorretagem')}
              >
                Alterar cocorretagem
              </Button>
            </div>
          ) : null}
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-ink-900">Resseguradores</p>
          {financeiro.reinsurers.length === 0 ? (
            <p className="text-sm text-ink-500">Sem resseguradores.</p>
          ) : (
            <ul className="flex flex-col gap-1 text-sm text-ink-700">
              {financeiro.reinsurers.map((item) => (
                <li key={item.id}>
                  {item.reinsurer.label} · {formatRate(item.share_bps)}
                </li>
              ))}
            </ul>
          )}
          {podeEditar ? (
            <div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEditando('resseguradores')}
              >
                Alterar resseguradores
              </Button>
            </div>
          ) : null}
        </div>
      </CardBody>
      {editando ? (
        <FinancialSharesDialog
          operacao={operacao}
          financeiro={financeiro}
          tipo={editando}
          onClose={() => setEditando(null)}
        />
      ) : null}
    </Card>
  );
}

function FinancialSharesDialog({
  operacao,
  financeiro,
  tipo,
  onClose,
}: {
  operacao: OperationDetail;
  financeiro: QuoteFinancial;
  tipo: 'cocorretagem' | 'resseguradores';
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const corretagem = tipo === 'cocorretagem';
  const [linhas, setLinhas] = useState<LinhaDeParte[]>(() =>
    corretagem
      ? financeiro.co_brokers.map((item) => ({
          chave: item.id,
          id: item.broker.id,
          participacao: rateToInput(item.share_bps),
        }))
      : financeiro.reinsurers.map((item) => ({
          chave: item.id,
          id: item.reinsurer.id,
          participacao: rateToInput(item.share_bps),
        })),
  );
  const [erroDoServidor, setErroDoServidor] = useState<ApiError | null>(null);

  const opcoesDeCorretora = useQuery({
    queryKey: ['brokers', { status: 'ativo', for: 'cocorretagem' }],
    queryFn: ({ signal }) => brokers.list({ status: 'ativo', limit: 100 }, signal),
    enabled: corretagem,
  });
  const opcoesDeRessegurador = useQuery({
    queryKey: ['reinsurers', 'opcoes'],
    queryFn: ({ signal }) => quoteFinancial.reinsurerOptions(signal),
    enabled: !corretagem,
    retry: false,
  });
  const opcoes = corretagem
    ? (opcoesDeCorretora.data?.items ?? []).map((item) => ({
        value: item.id,
        label: item.legal_name,
      }))
    : (opcoesDeRessegurador.data ?? [])
        .filter((item) => item.status === 'ativo')
        .map((item) => ({ value: item.id, label: item.legal_name }));
  // A parte que ja estava na cotacao continua na lista mesmo que tenha saido das opcoes.
  const atuais = corretagem
    ? financeiro.co_brokers.map((item) => ({
        value: item.broker.id,
        label: item.broker.label,
      }))
    : financeiro.reinsurers.map((item) => ({
        value: item.reinsurer.id,
        label: item.reinsurer.label,
      }));
  const todas = [
    ...opcoes,
    ...atuais.filter((a) => !opcoes.some((o) => o.value === a.value)),
  ];

  const invalida = linhas.some(
    (linha) => linha.id === '' || participacaoEmPontos(linha.participacao) === null,
  );

  const gravar = useMutation({
    mutationFn: async (): Promise<void> => {
      const itens = linhas.map((linha) => ({
        id: linha.id,
        share_bps: participacaoEmPontos(linha.participacao)!,
      }));
      if (corretagem) {
        await quoteFinancial.replaceCoBrokers(
          operacao.id,
          itens.map((item) => ({ broker_id: item.id, share_bps: item.share_bps })),
        );
        return;
      }
      await quoteFinancial.replaceReinsurers(
        operacao.id,
        itens.map((item) => ({ reinsurer_id: item.id, share_bps: item.share_bps })),
      );
    },
    onSuccess: () => {
      toast.success(corretagem ? 'Cocorretagem gravada.' : 'Resseguradores gravados.');
      void queryClient.invalidateQueries({ queryKey: chaveDoFinanceiro(operacao.id) });
      onClose();
    },
    onError: (error: Error) => {
      if (error instanceof ApiError) setErroDoServidor(error);
      toast.error(error.message);
    },
  });

  const prefixo = corretagem ? 'co_brokers' : 'reinsurers';
  const atualizar = (indice: number, campo: 'id' | 'participacao', valor: string) =>
    setLinhas(
      linhas.map((linha, i) => (i === indice ? { ...linha, [campo]: valor } : linha)),
    );

  return (
    <Modal
      open
      onClose={onClose}
      title={corretagem ? 'Alterar cocorretagem' : 'Alterar resseguradores'}
      description="A lista inteira substitui a gravada, na ordem. A participacao e um percentual com ate duas casas."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={gravar.isPending}
            disabled={invalida}
            onClick={() => gravar.mutate()}
          >
            Gravar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {!corretagem && opcoesDeRessegurador.isError ? (
          <Alert tone="warning" title="Resseguradores indisponiveis">
            Seu acesso nao le o cadastro de resseguradores da parametrizacao.
          </Alert>
        ) : null}
        {linhas.map((linha, indice) => (
          <div
            key={linha.chave}
            className="grid items-end gap-3 sm:grid-cols-[1fr_10rem_auto]"
          >
            <Select
              label={corretagem ? 'Corretora em cocorretagem' : 'Ressegurador'}
              placeholder="Selecione"
              value={linha.id}
              options={todas}
              error={fieldErrorOf(
                erroDoServidor,
                `${prefixo}[${indice}].${corretagem ? 'broker_id' : 'reinsurer_id'}`,
              )}
              onChange={(event) => atualizar(indice, 'id', event.target.value)}
            />
            <Field
              label={
                corretagem
                  ? 'Participacao da corretora (%)'
                  : 'Participacao do ressegurador (%)'
              }
              inputMode="decimal"
              value={linha.participacao}
              error={
                linha.participacao !== '' &&
                participacaoEmPontos(linha.participacao) === null
                  ? mensagemDaParticipacao
                  : fieldErrorOf(erroDoServidor, `${prefixo}[${indice}].share_bps`)
              }
              onChange={(event) =>
                atualizar(indice, 'participacao', event.target.value)
              }
            />
            <Button
              variant="ghost"
              onClick={() => setLinhas(linhas.filter((_, i) => i !== indice))}
            >
              Tirar
            </Button>
          </div>
        ))}
        <div>
          <Button
            variant="secondary"
            size="sm"
            disabled={linhas.length >= 20}
            onClick={() =>
              setLinhas([
                ...linhas,
                {
                  chave: `nova-${Date.now()}-${linhas.length}`,
                  id: '',
                  participacao: '',
                },
              ])
            }
          >
            {corretagem ? 'Acrescentar corretora' : 'Acrescentar ressegurador'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const opcoesDeCosseguro = [
  { value: 'cedido', label: 'Cedido' },
  { value: 'aceito', label: 'Aceito' },
];

const opcoesDeTipoDeEmissao = [
  { value: 'automatica', label: 'Automatica' },
  { value: 'manual', label: 'Manual' },
];

function FinancialIssuanceCard({
  operacao,
  financeiro,
}: {
  operacao: OperationDetail;
  financeiro: QuoteFinancial;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const permissoes = usePermissoesDoFinanceiro(operacao);
  const [cosseguro, setCosseguro] = useState(financeiro.coinsurance_type ?? '');
  const [tipo, setTipo] = useState(financeiro.issuance_kind ?? '');
  const [apoliceManual, setApoliceManual] = useState(financeiro.manual_policy_issuance);
  const [boletoManual, setBoletoManual] = useState(financeiro.manual_billet_issuance);
  const [externo, setExterno] = useState(financeiro.related_external_id ?? '');
  const [configurando, setConfigurando] = useState(false);
  const [erroDoServidor, setErroDoServidor] = useState<ApiError | null>(null);

  const podeCosseguro = permissoes.resseguro && financeiro.plan_editable;
  const podeEmissao = permissoes.financeiro && financeiro.issuance_editable;

  const mudancas: QuoteFinancialInput = {};
  if (cosseguro !== (financeiro.coinsurance_type ?? '')) {
    mudancas.coinsurance_type =
      cosseguro === '' ? null : (cosseguro as QuoteCoinsuranceType);
  }
  if (tipo !== (financeiro.issuance_kind ?? '')) {
    mudancas.issuance_kind = tipo === '' ? null : (tipo as QuoteIssuanceKind);
  }
  if (apoliceManual !== financeiro.manual_policy_issuance) {
    mudancas.manual_policy_issuance = apoliceManual;
  }
  if (boletoManual !== financeiro.manual_billet_issuance) {
    mudancas.manual_billet_issuance = boletoManual;
  }
  if (externo.trim() !== (financeiro.related_external_id ?? '')) {
    mudancas.related_external_id = externo.trim() === '' ? null : externo.trim();
  }
  const semMudanca = Object.keys(mudancas).length === 0;

  const gravar = useMutation({
    mutationFn: () => quoteFinancial.update(operacao.id, mudancas),
    onSuccess: () => {
      toast.success('Dados de emissao da cotacao gravados.');
      setErroDoServidor(null);
      void queryClient.invalidateQueries({ queryKey: chaveDoFinanceiro(operacao.id) });
    },
    onError: (error: Error) => {
      if (error instanceof ApiError) setErroDoServidor(error);
      toast.error(error.message);
    },
  });

  return (
    <Card>
      <CardHeader
        title="Cosseguro e emissao"
        description="Tipo de cosseguro, tipo de emissao, emissao manual e o identificador da proposta relacionada. Guardados sem efeito: as regras que os usariam ainda nao foram homologadas."
        action={
          permissoes.parametros ? (
            <Button variant="secondary" size="sm" onClick={() => setConfigurando(true)}>
              Configuracao financeira
            </Button>
          ) : null
        }
      />
      <CardBody className="grid gap-4 sm:grid-cols-2">
        <Select
          label="Tipo de cosseguro"
          placeholder="Sem cosseguro"
          value={cosseguro}
          disabled={!podeCosseguro}
          options={opcoesDeCosseguro}
          error={fieldErrorOf(erroDoServidor, 'coinsurance_type')}
          onChange={(event) => setCosseguro(event.target.value)}
        />
        <Select
          label="Tipo de emissao"
          placeholder="Nao informado"
          value={tipo}
          disabled={!podeEmissao}
          options={opcoesDeTipoDeEmissao}
          error={fieldErrorOf(erroDoServidor, 'issuance_kind')}
          onChange={(event) => setTipo(event.target.value)}
        />
        <Field
          label="Identificador da proposta relacionada"
          value={externo}
          maxLength={60}
          disabled={!podeEmissao}
          error={fieldErrorOf(erroDoServidor, 'related_external_id')}
          onChange={(event) => setExterno(event.target.value)}
        />
        <div className="flex flex-col justify-end gap-2">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={apoliceManual}
              disabled={!podeEmissao}
              onChange={(event) => setApoliceManual(event.target.checked)}
              className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
            />
            Emissao manual da apolice habilitada
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={boletoManual}
              disabled={!podeEmissao}
              onChange={(event) => setBoletoManual(event.target.checked)}
              className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
            />
            Emissao manual de boleto habilitada
          </label>
        </div>
        <p className="text-xs text-ink-500 sm:col-span-2">
          Etiquetas do laudo de emissao:{' '}
          {Object.keys(financeiro.issuance_tags).length === 0
            ? 'nao informadas (conteudo a confirmar).'
            : `${Object.keys(financeiro.issuance_tags).length} etiqueta(s).`}
          {financeiro.premium_insured_ratio_limit_bps !== null
            ? ` Percentual limite premio x IS da empresa: ${formatRate(financeiro.premium_insured_ratio_limit_bps)} (sem efeito).`
            : ''}
        </p>
        <FinancialCoinsuranceHistory operacao={operacao} />
        {podeCosseguro || podeEmissao ? (
          <div className="flex justify-end sm:col-span-2">
            <Button
              loading={gravar.isPending}
              disabled={semMudanca}
              onClick={() => gravar.mutate()}
            >
              Gravar dados de emissao
            </Button>
          </div>
        ) : null}
      </CardBody>
      {configurando ? (
        <FinancialSettingsDialog
          operacao={operacao}
          onClose={() => setConfigurando(false)}
        />
      ) : null}
    </Card>
  );
}

const rotuloDoCosseguro = (tipo: QuoteCoinsuranceType | null) =>
  tipo === null
    ? 'Sem cosseguro'
    : (opcoesDeCosseguro.find((opcao) => opcao.value === tipo)?.label ?? tipo);

/**
 * Historico do tipo de cosseguro da cotacao (GetProposalCoInsuranceHistories, COT-451):
 * cada mudanca gravada por alcada, com quem e quando. So leitura.
 */
function FinancialCoinsuranceHistory({ operacao }: { operacao: OperationDetail }) {
  const historico = useQuery({
    queryKey: [...chaveDoFinanceiro(operacao.id), 'historico-do-cosseguro'],
    queryFn: ({ signal }) => quoteFinancial.coinsuranceHistory(operacao.id, signal),
  });

  return (
    <div className="sm:col-span-2">
      <p className="text-sm font-medium text-ink-700">Historico do cosseguro</p>
      {historico.isPending ? (
        <p className="text-xs text-ink-500">Carregando o historico...</p>
      ) : historico.isError ? (
        <p className="text-xs text-ink-500">
          Nao foi possivel ler o historico do cosseguro.
        </p>
      ) : historico.data.items.length === 0 ? (
        <p className="text-xs text-ink-500">Nenhuma mudanca do tipo de cosseguro.</p>
      ) : (
        <ul className="mt-1 space-y-1 text-xs text-ink-600">
          {historico.data.items.map((item) => (
            <li key={item.id}>
              {formatDateTime(item.changed_at)}: {rotuloDoCosseguro(item.previous_type)}
              {' para '}
              {rotuloDoCosseguro(item.coinsurance_type)}
              {item.changed_label ? ` (${item.changed_label})` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Configuracao financeira da cotacao da empresa: o aceite exigido na emissao (EMI-001,
 * nasce desligado) e o percentual limite da relacao premio x IS (COT-123, sem efeito).
 */
function FinancialSettingsDialog({
  operacao,
  onClose,
}: {
  operacao: OperationDetail;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const configuracao = useQuery({
    queryKey: ['operations', 'financial-settings'],
    queryFn: ({ signal }) => quoteFinancial.settings(signal),
  });
  const [exige, setExige] = useState<boolean | null>(null);
  const [limite, setLimite] = useState<string | null>(null);

  const exigeAtual =
    exige ?? configuracao.data?.issuance_requires_terms_acceptance ?? false;
  const limiteAtual =
    limite ??
    (configuracao.data?.premium_insured_ratio_limit_bps
      ? rateToInput(configuracao.data.premium_insured_ratio_limit_bps)
      : '');
  const limiteInvalido = limiteAtual !== '' && parseRate(limiteAtual) === null;

  const gravar = useMutation({
    mutationFn: () =>
      quoteFinancial.updateSettings({
        issuance_requires_terms_acceptance: exigeAtual,
        premium_insured_ratio_limit_bps:
          limiteAtual === '' ? null : parseRate(limiteAtual),
      }),
    onSuccess: () => {
      toast.success('Configuracao financeira gravada.');
      void queryClient.invalidateQueries({
        queryKey: ['operations', 'financial-settings'],
      });
      void queryClient.invalidateQueries({ queryKey: chaveDoFinanceiro(operacao.id) });
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Configuracao financeira"
      description="Vale para todas as cotacoes da empresa, daqui em diante, e fica na trilha."
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={gravar.isPending}
            disabled={configuracao.isPending || limiteInvalido}
            onClick={() => gravar.mutate()}
          >
            Gravar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            checked={exigeAtual}
            onChange={(event) => setExige(event.target.checked)}
            className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
          />
          Exigir o aceite do termo de emissao antes de emitir
        </label>
        <p className="text-xs text-ink-500">
          A regra (EMI-001) ainda nao foi homologada: ligar e decisao da empresa.
        </p>
        <Field
          label="Percentual limite premio x IS (%)"
          inputMode="decimal"
          value={limiteAtual}
          error={limiteInvalido ? 'Use um percentual com ate duas casas.' : undefined}
          onChange={(event) => setLimite(event.target.value)}
        />
      </div>
    </Modal>
  );
}

const validacoes: { tipo: QuoteValidationKind; rotulo: string }[] = [
  { tipo: 'pep-validation', rotulo: 'Validar PEP' },
  { tipo: 'reinsurance-validation', rotulo: 'Validar resseguro' },
  { tipo: 'underwriting-validation', rotulo: 'Validar subscricao' },
];

type Consulta = 'retroatividade' | 'cosseguro' | 'prazo';

function FinancialValidationsCard({ operacao }: { operacao: OperationDetail }) {
  const permissoes = usePermissoesDoFinanceiro(operacao);
  const [resultado, setResultado] = useState<{ titulo: string; texto: string } | null>(
    null,
  );
  const [consulta, setConsulta] = useState<Consulta | null>(null);

  const validar = useMutation({
    mutationFn: (tipo: QuoteValidationKind) =>
      quoteFinancial.validate(operacao.id, tipo),
    onSettled: (_dados, erro, tipo) => {
      const rotulo = validacoes.find((v) => v.tipo === tipo)?.rotulo ?? 'Validacao';
      const regras = regrasPendentesDoErro(erro);
      setResultado({
        titulo: rotulo,
        texto:
          regras.length > 0
            ? `${erro instanceof Error ? erro.message : ''} Regras pendentes: ${regras.join(', ')}.`
            : erro instanceof Error
              ? erro.message
              : 'Validacao concluida.',
      });
    },
  });

  const retroatividade = useQuery({
    queryKey: ['operations', operacao.id, 'retroactivity-validation'],
    queryFn: ({ signal }) => quoteFinancial.retroactivity(operacao.id, signal),
    enabled: consulta === 'retroatividade',
  });
  const cosseguro = useQuery({
    queryKey: ['operations', operacao.id, 'coinsurance-validation'],
    queryFn: ({ signal }) => quoteFinancial.coinsurance(operacao.id, signal),
    enabled: consulta === 'cosseguro',
  });
  const prazo = useQuery({
    queryKey: [
      'modalities',
      operacao.modality.id,
      'term-validation',
      operacao.term_days,
    ],
    queryFn: ({ signal }) =>
      quoteFinancial.termCheck(
        operacao.modality.id,
        operacao.term_days ?? null,
        signal,
      ),
    enabled: consulta === 'prazo',
  });

  const nulo = (valor: number | null | undefined) =>
    valor === null || valor === undefined ? 'nao informado' : String(valor);

  return (
    <Card>
      <CardHeader
        title="Validacoes automaticas"
        description="PEP, resseguro e subscricao respondem com as regras pendentes ate a homologacao. Retroatividade, cosseguro e prazo mostram o que existe, sem decidir."
      />
      <CardBody className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          {validacoes.map((validacao) => (
            <Button
              key={validacao.tipo}
              variant="secondary"
              size="sm"
              disabled={!permissoes.porta}
              loading={validar.isPending && validar.variables === validacao.tipo}
              onClick={() => validar.mutate(validacao.tipo)}
            >
              {validacao.rotulo}
            </Button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConsulta('retroatividade')}
          >
            Retroatividade
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setConsulta('cosseguro')}>
            Cosseguro
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setConsulta('prazo')}>
            Prazo da modalidade
          </Button>
        </div>

        {resultado ? (
          <Alert tone="warning" title={resultado.titulo}>
            {resultado.texto}
          </Alert>
        ) : null}

        {consulta === 'retroatividade' && retroatividade.data ? (
          <Alert tone="info" title="Retroatividade (sem decisao)">
            Inicio da vigencia: {formatCivilDate(retroatividade.data.coverage_start)}.
            Hoje: {formatCivilDate(retroatividade.data.reference_date)}.{' '}
            {retroatividade.data.starts_before_reference
              ? 'O inicio e anterior a hoje.'
              : 'O inicio nao e anterior a hoje.'}{' '}
            Maximo de dias retroativos da modalidade:{' '}
            {nulo(retroatividade.data.modality_limits?.max_retroactive_days)}. Regras
            pendentes: {retroatividade.data.rules.pending_rules.join(', ')}.
          </Alert>
        ) : null}
        {consulta === 'cosseguro' && cosseguro.data ? (
          <Alert tone="info" title="Cosseguro (sem decisao)">
            Tipo da cotacao: {cosseguro.data.coinsurance_type ?? 'sem cosseguro'}.
            Cosseguradoras da cotacao:{' '}
            {cosseguro.data.coinsurers.length === 0
              ? 'nenhuma cosseguradora informada'
              : cosseguro.data.coinsurers
                  .map(
                    (c) =>
                      `${c.name}${c.share_bps !== null ? ` (${formatRate(c.share_bps)})` : ''}`,
                  )
                  .join('; ')}
            . Regras pendentes: {cosseguro.data.rules.pending_rules.join(', ')}.
          </Alert>
        ) : null}
        {consulta === 'prazo' && prazo.data ? (
          <Alert tone="info" title="Prazo da modalidade (sem decisao)">
            Prazo da cotacao: {nulo(prazo.data.term_days)} dias. Minimo da modalidade:{' '}
            {nulo(prazo.data.modality_limits.min_term_days)}; maximo:{' '}
            {nulo(prazo.data.modality_limits.max_term_days)}. Regras pendentes:{' '}
            {prazo.data.rules.pending_rules.join(', ')}.
          </Alert>
        ) : null}
        {[retroatividade, cosseguro, prazo].some((q) => q.isError) ? (
          <Alert tone="danger" title="Consulta indisponivel">
            Nao foi possivel consultar agora.
          </Alert>
        ) : null}
      </CardBody>
    </Card>
  );
}
